// Company Handbook - Policies and Procedures
// Updated with comprehensive content

export const policyCategories = [
  {
    id: 'overview',
    label: 'Overview',
    icon: '📋',
    content: `
## Welcome to the Company Handbook

This handbook is your primary reference for understanding how we work, what's expected of you as an Executive Assistant, and how to operate effectively within our systems. It is designed to support you in your role while providing clarity, structure, and consistency across day-to-day operations.

This is a living resource. You are encouraged to revisit it regularly and use it whenever you are unsure about a process, expectation, or best practice.

### Purpose

This handbook exists to:

- Clearly define expectations and responsibilities associated with your role
- Outline professional standards, communication norms, and conduct guidelines
- Explain how to use company systems, tools, and workflows correctly
- Provide guidance for common scenarios you may encounter as an assistant
- Protect both you and the company through consistent procedures

Some sections are informational, while others outline required policies. When in doubt, follow the guidance here or escalate questions to your executive or admin.

### Updates & Changes

This handbook is updated periodically to reflect changes in operations, tools, or policies.

When significant updates are made:
- You will be notified via dashboard notifications
- Important changes may also be communicated directly through system messages
- Continued use of the platform indicates acknowledgment of the most current version.
    `
  },
  {
    id: 'code-of-conduct',
    label: 'Code of Conduct',
    icon: '⚖️',
    content: `
## Code of Conduct

As an Executive Assistant, you act as an extension of the executive(s) you support and the company as a whole. Your conduct directly reflects the organization. Professionalism, discretion, and accountability are not optional — they are core requirements of this role.

This Code of Conduct applies to all work performed, including:
- Dashboard activity
- Messaging and communications
- Travel planning
- Calendar management
- Document handling
- Internal and external interactions

### Professional Standards

You are expected to:
- Act with integrity, reliability, and sound judgment at all times
- Maintain a calm, professional demeanor, even in high-pressure situations
- Follow established workflows and escalation paths
- Use company tools and systems as intended

If you are unsure how to handle a situation, pause and escalate rather than assume.

### Confidentiality & Information Security

Confidentiality is critical in this role.

You must:
- **Never share** confidential or sensitive information outside authorized channels
- Treat all executive information as private by default
- Secure documents, files, and credentials at all times
- Avoid discussing executive matters in public or unsecured environments

Sensitive information includes (but is not limited to):
- Travel details
- Personal preferences
- Financial information
- Business strategy
- Login credentials
- Internal communications

If you believe information has been exposed, compromised, or mishandled, report it immediately through the appropriate admin or executive channel.

### Communication Standards

Clear, timely communication is essential.

**Expectations:**
- Respond to messages within 4 business hours unless marked urgent
- Urgent matters should be acknowledged as soon as possible
- Use professional, respectful language at all times
- Keep executives informed of time-sensitive or high-impact issues
- Document key decisions, approvals, or changes within the system

When in doubt, over-communicate rather than under-communicate.

### Workplace Behavior & Boundaries

You are expected to:
- Treat all individuals with respect and professionalism
- Maintain appropriate boundaries with executives, coworkers, vendors, and partners
- Follow a zero-tolerance policy for discrimination, harassment, or retaliation

Any concerns regarding conduct — including your own or others' — should be escalated promptly through the appropriate administrative or HR channel.

### Accountability

Failure to follow this Code of Conduct may result in:
- Restricted system access
- Review or corrective action
- Termination of role or contract

This is not punitive — it exists to protect you, the executive(s), and the company.
    `
  },
  {
    id: 'time-attendance',
    label: 'Time & Attendance',
    icon: '⏰',
    content: `
## Time & Attendance

Accurate time tracking is required for all assistants. This ensures transparency, proper workload management, and accurate records for both the assistant and the company.

All assistants are expected to follow the time and attendance procedures outlined below.

### Work Hours

- Standard working hours are Monday–Friday, 9:00 AM – 5:00 PM, unless otherwise agreed upon with your assigned executive
- Flexibility may be required based on executive schedules, travel, or urgent needs
- Any expected deviation from agreed hours should be communicated in advance when possible
- Overtime or extended availability must be approved ahead of time by the executive or admin

### Time Tracking Requirements

All work time must be recorded using the built-in Time Clock system.

You are required to:
- Clock in at the start of your workday
- Clock out at the end of your workday
- Clock out for unpaid breaks when applicable
- Ensure your time entries accurately reflect hours worked

Time entries are visible to admin and subject to review.

### Manual Time Entries

If you forget to clock in or out:
- Submit a manual time entry through the Time Clock system
- Manual entries must be submitted within 24 hours
- Include a brief explanation for the adjustment
- Manual entries require approval before being finalized

Repeated missed clock-ins may be reviewed for compliance.

### Breaks

- A 30-minute unpaid lunch break is required for shifts of 6 hours or more
- Two 15-minute paid breaks are permitted during an 8-hour shift
- Break timing should be coordinated with workload and executive needs
- Breaks should not disrupt time-sensitive responsibilities

### Absences & Availability

If you are unable to work as scheduled:
- Notify your executive and admin as soon as possible
- Use the appropriate leave or absence request process when applicable
- Provide documentation for extended or planned absences if requested

Failure to communicate absences in a timely manner may impact scheduling and workload planning.

### Accuracy & Accountability

- Time records must be accurate and truthful
- Intentionally falsifying time entries is not permitted
- Abuse of the time tracking system may result in corrective action

This policy exists to ensure fairness, clarity, and proper support — not micromanagement.
    `
  },
  {
    id: 'communication',
    label: 'Communication Guidelines',
    icon: '💬',
    content: `
## Communication Guidelines

Clear, timely communication is essential to your role as an Executive Assistant. You are expected to proactively manage communication using the tools provided in the platform and maintain consistent visibility for your executive and admin.

### In-App Messaging (Primary Communication Tool)

The built-in messaging system is the primary channel for daily communication.

You are expected to:
- Check messages at least three times per workday (morning, midday, end of day)
- Respond to urgent messages within 1 hour during working hours
- Use message threads to keep conversations organized by topic
- Mark messages as read after reviewing them
- Ask clarifying questions when instructions are unclear rather than assuming

**Urgent items include:**
- Same-day scheduling changes
- Travel disruptions
- Time-sensitive approvals
- Executive requests marked as urgent

Non-urgent items should still be acknowledged within a reasonable timeframe.

### Email Communication (When Required)

While most communication should occur inside the platform, email may be required for external parties.

When handling email:
- Use professional subject lines and greetings
- Keep messages concise and well-structured
- Proofread before sending
- Reply within 24 business hours, unless otherwise instructed
- Set an out-of-office message if you will be unavailable

If an email involves an important decision or confirmation, document the outcome in the platform.

### Meetings & Calendar Coordination

Meetings are a core responsibility and must be handled with care.

**Expectations:**
- Arrive at least 5 minutes early to all meetings
- Review the agenda or purpose beforehand
- Have relevant materials prepared and accessible
- Take clear, concise notes during meetings
- Record action items and next steps
- Schedule follow-ups or reminders when needed

Meeting notes and outcomes should be logged promptly.

### Documentation & Record Keeping

Accurate documentation ensures continuity and accountability.

You are responsible for:
- Logging key decisions, approvals, and changes
- Keeping executives informed of progress and blockers
- Using company-approved tools for storing documents
- Avoiding personal storage systems (personal email, personal drives)
- Archiving completed projects appropriately

If something impacts timelines, costs, or executive availability, it should be documented.

### Professional Tone & Clarity

All communication should:
- Be professional, respectful, and clear
- Avoid slang, sarcasm, or informal language
- Maintain discretion at all times
- Reflect that you are representing the executive and the company

If you are unsure how to phrase something, default to clarity and professionalism.
    `
  },
  {
    id: 'travel-expenses',
    label: 'Travel & Expenses',
    icon: '✈️',
    content: `
## Travel & Expenses

Travel planning and expense coordination are core responsibilities of the Executive Assistant role. Accuracy, organization, cost awareness, and proactive communication are essential at all times.

### Travel Planning & Booking

Whenever possible, travel should be booked at least two weeks in advance. Short-notice travel may occur and should be handled promptly and with care.

When booking travel:
- Prioritize the executive's time, comfort, and schedule
- Choose cost-effective options without sacrificing reliability
- Use approved vendors whenever available
- Apply common sense when deciding between driving and flying

Always double-check:
- Dates and times
- Departure and arrival locations
- Time zones
- Ground transportation needs
- Hotel proximity to meetings, offices, or event venues

If any detail is unclear, confirm before booking.

### Approved Travel Vendors

The following vendors are pre-approved and should be used whenever possible.

**✈️ Airlines (Preferred)**
- Delta Air Lines (primary preference)
- American Airlines
- United Airlines
- Southwest Airlines (for regional or short-haul trips)

When booking flights:
- Prioritize nonstop flights when available
- Select reasonable departure and arrival times
- Avoid extreme layovers unless cost savings are significant

**🏨 Hotels (Preferred)**
- Four Seasons
- Ritz-Carlton
- Marriott (including Marriott Marquis, JW Marriott, Westin)
- Hilton (including Conrad, Waldorf Astoria)
- Hyatt (including Grand Hyatt, Andaz)

Guidelines:
- Location matters more than luxury
- Avoid budget hotels unless specifically approved
- Ensure the hotel supports business travel needs

**🚗 Ground Transportation & Car Rentals**
- Enterprise Rent-A-Car
- Hertz
- Avis
- National Car Rental

Other options:
- Personal vehicle (when appropriate)
- Uber / Lyft for local transportation
- Black car service for executive-level travel

### Travel Binder (Required)

All travel-related information must be stored in the Travel Binder.

Each trip entry must include:
- Travel type
- Vendor name
- Confirmation number
- Dates and times
- Addresses and contact information
- Relevant notes

Incomplete travel entries are not acceptable.

### Calendar Coordination

Every trip must be reflected on the calendar.

This includes:
- Travel days (departures and returns)
- "Business Travel" or "Out of Office" blocks
- Major meetings or events tied to the trip

The Travel Binder and calendar should always match.

### Expense Tracking & Reporting

Expenses must be tracked as they occur.

**Requirements:**
- Submit expenses within 7 days of travel
- Upload itemized receipts
- Use accurate expense categories
- Follow company reimbursement procedures

Late or incomplete expense submissions may delay reimbursement.

### Per Diem & Meal Expenses

**Rules:**
- Alcohol expenses require advance approval
- Receipts are required for expenses over $25
- Personal expenses should never be submitted

When uncertain, ask before spending.
    `
  },
  {
    id: 'technology',
    label: 'Technology Use',
    icon: '💻',
    content: `
## Technology Use

Technology is the backbone of how this company operates. Proper use of technology is not optional — it is a core job responsibility.

### 1. Acceptable Use of Company Technology

Company technology includes:
- Company dashboards and portals
- Messaging systems
- Calendars and scheduling tools
- Cloud storage
- Company email accounts
- Travel and vendor platforms

**Primary Use**

Company technology must be used primarily for business purposes.

**Limited Personal Use**

Limited personal use is permitted only if:
- It does not interfere with work responsibilities
- It does not consume excessive time or resources
- It does not involve sensitive data
- It does not violate any other policy

### 2. Prohibited Uses

You may not use company systems for:
- Personal businesses or freelance work
- Unauthorized automation or data extraction
- Downloading pirated or unlicensed software
- Accessing illegal, explicit, or inappropriate content
- Sharing company data with unauthorized parties
- Bypassing security controls

If you are unsure whether something is permitted, do not proceed without approval.

### 3. Account Security & Password Management

**Password Requirements**

All passwords must:
- Be at least 12 characters long
- Include uppercase and lowercase letters
- Include numbers and symbols
- Be unique (not reused across systems)

Passwords must never be:
- Shared with anyone
- Written on paper
- Saved in unsecured notes

**Two-Factor Authentication (2FA)**

- 2FA must be enabled wherever available
- Never approve login requests you did not initiate
- Report unexpected login alerts immediately

### 4. Device & Session Security

**While Working**
- Lock your screen whenever you step away
- Do not leave devices unattended in public places
- Avoid working where screens can be easily viewed

**Public & Remote Use**
- Avoid public Wi-Fi when possible
- Do not access sensitive systems on unsecured networks

**Lost or Stolen Devices**

Report immediately, regardless of time. Do not wait.

### 5. Data Storage & File Management

**Approved Storage**

You must:
- Save files only in approved company cloud storage
- Follow naming conventions when provided
- Organize files logically
- Keep folders clean

You may not:
- Store company files on personal devices
- Email files to personal email accounts
- Use personal cloud storage unless explicitly approved

**Sensitive Data Handling**

Sensitive data must:
- Never be copied unnecessarily
- Never be stored outside approved systems
- Never be shared casually or verbally in public

### 6. Software, Tools & Applications

Only approved software may be used for company work.

Before installing or using a new tool:
- Identify the problem it solves
- Document how it would be used
- Request approval
- Wait for confirmation

### 7. Monitoring, Logging & Accountability

Company systems may log:
- Logins
- Activity
- Changes
- Access history

You are expected to:
- Operate transparently
- Follow access permissions
- Respect audit processes
- Cooperate with reviews

### 8. Errors, Issues & Reporting

You must report:
- Bugs or system errors
- Accidental data access
- Incorrect data entry
- Security concerns
- Unusual system behavior

Reporting early will not result in punishment. Hiding issues may.

### 9. When You're Unsure

If you are ever unsure about whether something is allowed, pause and ask. Do not assume.
    `
  },
  {
    id: 'performance',
    label: 'Performance & Development',
    icon: '📈',
    content: `
## Performance & Development

Your performance as an Executive Assistant is evaluated based on reliability, communication, judgment, organization, and your ability to anticipate and support executive needs.

### 1. Performance Reviews

Performance reviews are conducted quarterly, with a more comprehensive annual review.

**What Reviews Are Based On**

- Quality and accuracy of work
- Timeliness and follow-through
- Communication clarity and responsiveness
- Organization and documentation habits
- Calendar, travel, and task management effectiveness
- Professionalism and discretion
- Ability to adapt to changing priorities
- Feedback from the executive(s) you support

**Review Process**

During a review:
- Strengths and areas for improvement will be discussed
- Specific examples will be referenced
- Expectations will be clarified
- Questions and concerns may be raised

At the end of each review:
- A short action plan may be created
- Clear next steps will be outlined
- Support or resources will be identified if needed

### 2. Goal Setting & Expectations

**Goal Alignment**

Goals should:
- Support executive priorities
- Improve efficiency or reliability
- Enhance communication or organization
- Reduce errors or rework
- Increase autonomy over time

Examples:
- Improve response time consistency
- Reduce scheduling conflicts
- Improve travel planning accuracy
- Enhance documentation quality
- Anticipate executive needs more proactively

**SMART Goals**

Goals should be:
- **S**pecific – clearly defined
- **M**easurable – progress can be tracked
- **A**chievable – realistic within your role
- **R**elevant – tied to executive or company needs
- **T**ime-bound – have a defined timeframe

Goals are reviewed monthly and may be adjusted as priorities change.

### 3. Ongoing Feedback

Feedback is not limited to formal reviews.

You are encouraged to:
- Ask for feedback regularly
- Clarify expectations when unsure
- Address misunderstandings early
- Treat feedback as guidance, not criticism

Feedback should be received professionally and used to improve performance.

### 4. Training & Skill Development

Professional development is encouraged and supported.

**Training Opportunities**

Development may include:
- Online courses
- Certifications
- Workshops or conferences
- Skill-specific training

Requests for training should include:
- What the training covers
- How it applies to your role
- Expected benefit to the executive or company

Approval is required before enrollment.

**Applying New Skills**

If training is approved:
- Apply new skills directly to your work
- Share useful insights or improvements
- Suggest workflow enhancements when appropriate

Learning is only valuable if it improves execution.

### 5. Growth & Advancement

Performance directly impacts:
- Level of trust and responsibility
- Scope of tasks assigned
- Autonomy in decision-making
- Opportunities for expanded roles or compensation adjustments

Assistants who demonstrate consistency, strong judgment, proactive problem-solving, clear communication, and reliability under pressure are more likely to receive expanded responsibilities and long-term growth opportunities.

### 6. Continuous Improvement Mindset

This role values improvement over perfection.

You are expected to:
- Learn from mistakes
- Adjust processes when needed
- Ask questions
- Stay organized
- Look for ways to work smarter, not just harder

Progress matters more than speed.
    `
  },
  {
    id: 'resources',
    label: 'Resources & Support',
    icon: '🆘',
    content: `
## Resources & Support

You are never expected to figure everything out alone. Knowing who to contact and when is part of being effective in this role.

### 1. Who to Contact

**Your Executive**

Contact for:
- Daily priorities and task direction
- Scheduling decisions
- Travel preferences and approvals
- Urgent or time-sensitive matters
- Clarifying expectations

**Admin / Manager**

Contact for:
- Policy questions or clarification
- Time off requests or scheduling concerns
- Escalations that cannot be resolved directly
- Questions about responsibilities or scope
- Issues impacting performance or workload

**IT Support**

Contact for:
- Login or access issues
- System errors or bugs
- Dashboard or tool malfunctions
- File upload or download problems
- Device or software-related issues

📧 **IT Helpdesk Email:** its@bm-assist.co

When contacting IT, include:
- A brief description of the issue
- What you were trying to do
- Any error messages shown
- Screenshots if applicable

**Human Resources (HR)**

Contact for:
- Workplace concerns
- Confidential matters
- Policy interpretation
- Employment-related questions
- Professional conduct concerns

📧 **HR Contact Email:** hr@bm-assist.co

HR communications are treated confidentially.

### 2. Tools & Systems

**Dashboard**

Your dashboard is your central command center. It is where you:
- View tasks and priorities
- Access messages
- Review calendar events
- Monitor notifications
- Navigate all work-related areas

You should start and end each workday here.

**Time Clock**

The Time Clock is used to:
- Clock in and out
- Track daily work hours
- Submit manual entries if needed
- View time history and approvals

**Travel Binder**

The Travel Binder is used to:
- Store executive travel details
- Manage itineraries, flights, hotels, and transportation
- Track confirmation numbers and vendor information
- Coordinate travel-related calendar blocks

**Messages**

The messaging system is the primary communication channel.

Use it to:
- Communicate with executives
- Receive instructions
- Ask clarifying questions
- Share updates or confirmations
- Receive system and administrative notifications

### 3. Additional Resources

You may also have access to:
- Help Center (FAQs and troubleshooting)
- Training materials or onboarding guides
- Company handbook and policies
- Tool-specific instructions or walkthroughs

These resources are intended to help you work more confidently and independently over time.

### 4. When You Need Help

If you are unsure:
- Who to contact
- How to proceed
- Whether something is urgent
- Whether an action is allowed

Pause and ask.

Asking for clarification is always better than guessing.
    `
  }
];
